import React from "react"
import clsx from "clsx"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {IconName} from "@fortawesome/pro-regular-svg-icons"
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react"


import {ILabel, IQuestion, QuestionField, TGameModes, VoteButtonType, VoteData} from "@backend/types/modules"
import {confidenceLevels} from "@backend/constants"


type HistoryProps = {
    preview?: boolean,
    mode: TGameModes,
    history: VoteData,
    onVoteChange?: ({questionId, labelId, voteId, newVote}) => void,
    voteButtons?: VoteButtonType[]
}

const textByMode: Record<TGameModes, (data: {
    text?: string,
    input?: string,
    output?: string
}) => React.ReactNode> = {
    basic: (data) => <div className="prose max-w-full">
        <h2 className="text-base font-medium text-gray-900 border-b mb-2 pb-2">Text</h2>
        <p>{data.text}</p>
    </div>,
    compare: (data) => <>
        <div className="prose max-w-full">
            <h2 className="text-base font-medium text-gray-900 border-b mb-2 pb-2">Input</h2>
            <p>{data.input}</p>
        </div>
        <div className="prose max-w-full">
            <h2 className="text-base font-medium text-gray-900 border-b mb-2 pb-2">Output</h2>
            <p>{data.output}</p>
        </div>
    </>,
    battle: (data) => <div>not implemented</div>,
    //childCheck: (data) => <div>not implemented yet</div>
}

export function History({preview, mode, history, onVoteChange, voteButtons}: HistoryProps) {


    return <Accordion allowMultiple>
        {Object.keys(history).map((qId) => (
            <AccordionItem key={qId}>
                <h2>
                    <AccordionButton>
                        <Box as="span" flex="1" textAlign="left" className="line-clamp-1">
                            {(history[qId][0].questionId as IQuestion).texts[0].text}
                        </Box>
                        <AccordionIcon/>
                    </AccordionButton>
                </h2>
                <AccordionPanel pb={4} className="space-y-4">
                    {(history[qId][0].questionId as IQuestion).texts[0].text}
                    <div>
                        <h2 className="text-base font-medium text-gray-900 border-b mb-2 pb-2">Votes</h2>
                        <TableContainer>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Question</Th>
                                        <Th>Your Vote (Click to
                                            Change) {preview ? " Preview mode: Votes are not saving" : ""}</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {history[qId].map(({_id, labelId, vote, voteId}) => (
                                        <Tr key={_id}>
                                            <Td>
                                                {(labelId as ILabel).questionStart}{` `}
                                                <strong>{(labelId as ILabel).questionEnd}</strong>
                                            </Td>
                                            <Td className="space-x-2">
                                                {voteButtons.map((voteBtn) =>
                                                    <Button
                                                        key={voteBtn.key}
                                                        variant={voteBtn.key === vote ? "solid" : "outline"}
                                                        colorScheme={voteBtn.colorScheme}
                                                        bg={voteBtn.key === vote ? `${voteBtn.colorScheme}.500` : null}
                                                        leftIcon={<FontAwesomeIcon
                                                            icon={["fal", voteBtn.icon as IconName]}/>}
                                                        className="col-span-1"
                                                        color={voteBtn.key === vote ? "white" : null}
                                                        onClick={onVoteChange.bind(null, {
                                                            questionId: qId,
                                                            labelId: (labelId as ILabel)?._id,
                                                            newVote: voteBtn.key,
                                                            voteId,
                                                        })}
                                                        size="xs">
                                                        {voteBtn.text}
                                                    </Button>
                                                )}
                                            </Td>
                                        </Tr>)
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </div>
                </AccordionPanel>
            </AccordionItem>)
        )}
    </Accordion>
}

export function BattleModeHistory({voteData, fields, questionIds}: {
    voteData: VoteData,
    fields: QuestionField[],
    questionIds: IQuestion[]
}) {


    return <div className="divide-y space-y-10">
        {Object.keys(voteData).map((qId) => {
            const question = questionIds.find(q => q._id === qId)

            if (!question)
                return <span>Error: no question</span>

            const texts = question.texts.reduce((acc, text) => {

                acc[text.optionKey] = text

                return acc
            }, {})

            return <div key={qId} className="grid grid-cols-3 gap-2 pt-6">
                <div className="col-span-1">
                    <span className="text-base font-semibold leading-7 text-gray-900">Question</span>
                    <p className="text-base leading-7 text-gray-600">
                        {texts["input"].text}
                    </p>
                </div>
                <div className="col-span-2 space-y-6">
                    {voteData[qId].map((vote) => (
                        <div key={vote._id}
                             className="sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-16 sm:space-y-0 lg:gap-x-10">
                            {vote.modelFieldIds.map(modelId => {
                                const model = fields.find(x => x._id === modelId)
                                return <div key={modelId}
                                            className={clsx("col-span-1 px-2 py-1 space-y-2", vote.vote === modelId && "border-2 border-green-500 rounded-lg")}>
                                    <h3
                                        className="text-base font-semibold leading-7 text-gray-900">{model.title || model.keyword || model.fileHeader}
                                    </h3>
                                    {vote.vote === modelId &&
                                      <Box
                                        borderColor={confidenceLevels[vote.confidenceLevel].colorCode}
                                        color={confidenceLevels[vote.confidenceLevel].colorCode}
                                        className="border rounded flex justify-start items-center space-x-2 px-2">
                                        <FontAwesomeIcon className="w-10 h-10" fixedWidth
                                                         icon={["fal", confidenceLevels[vote.confidenceLevel].icon]}/>
                                        <span>{confidenceLevels[vote.confidenceLevel].title}</span>
                                      </Box>
                                    }
                                    <p className="text-base leading-7 text-gray-600 line-clamp-3">{texts[model.optionKey].text}</p>
                                </div>
                            })}
                        </div>
                    ))}
                </div>
            </div>
        })}
    </div>
}
